import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "../../../contexts/AuthContext";
import { tenantsApi } from "../../../services/api";
import { integrationsApi } from "../../../services/api/integrations";
import Icon from "../../../components/AppIcon";

const TenantManagement = () => {
  const { isGlobalAdmin } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const response = await tenantsApi.getAll();
      const data = response.data ?? response.Data ?? [];
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load tenants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isGlobalAdmin) {
      fetchTenants();
    }
  }, [isGlobalAdmin]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleDeleteTenant = async (tenantId, tenantName) => {
    if (
      !confirm(
        `Are you sure you want to delete "${tenantName}"? This will also delete all users and data associated with this tenant.`,
      )
    )
      return;

    try {
      await tenantsApi.delete(tenantId);
      showNotification("Tenant deleted successfully");
      fetchTenants();
    } catch (err) {
      showNotification(err.message || "Failed to delete tenant", "error");
    }
  };

  if (!isGlobalAdmin) {
    return (
      <div className="p-6">
        <div className="card p-8 text-center">
          <Icon
            name="ShieldAlert"
            size={48}
            className="mx-auto mb-4 text-warning"
          />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            Only global administrators can access tenant management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Tenant Management - Rocket Tronc</title>
      </Helmet>

      <div className="p-6">
        {/* Notification */}
        {notification && (
          <div
            className={`mb-6 alert ${notification.type === "error" ? "alert-error" : "alert-success"}`}
          >
            <Icon
              name={
                notification.type === "error" ? "AlertCircle" : "CheckCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-bold">
              Tenant Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage customer organizations and their subscriptions
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setIsAddModalOpen(true)}
          >
            <Icon name="Building2" size={18} />
            Add Tenant
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="card p-4">
            <p className="text-sm text-muted-foreground">Total Tenants</p>
            <p className="text-2xl font-bold font-data">{tenants.length}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold font-data text-success">
              {tenants.filter((t) => t.isActive).length}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-muted-foreground">Total Users</p>
            <p className="text-2xl font-bold font-data">
              {tenants.reduce((sum, t) => sum + (t.userCount || 0), 0)}
            </p>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="card p-8 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">Loading tenants...</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="alert alert-error mb-6">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
            <button
              className="btn btn-outline btn-sm ml-auto"
              onClick={fetchTenants}
            >
              Retry
            </button>
          </div>
        )}

        {/* Tenants Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tenants.length === 0 ? (
              <div className="col-span-full card p-8 text-center">
                <Icon
                  name="Building2"
                  size={48}
                  className="mx-auto mb-4 text-muted-foreground"
                />
                <p className="text-muted-foreground">No tenants found</p>
              </div>
            ) : (
              tenants.map((tenant) => (
                <div key={tenant.id} className="card">
                  <div className="p-4 border-b border-border">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{
                            backgroundColor:
                              tenant.primaryColor || "var(--color-primary)",
                            opacity: 0.1,
                          }}
                        >
                          <Icon
                            name="Building"
                            size={24}
                            style={{
                              color:
                                tenant.primaryColor || "var(--color-primary)",
                            }}
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold">{tenant.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {tenant.slug}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`badge ${tenant.isActive ? "badge-success" : "badge-neutral"}`}
                      >
                        {tenant.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Contact</span>
                      <span>{tenant.contactEmail}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Plan</span>
                      <span className="badge badge-secondary">
                        {tenant.subscriptionPlan}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Users</span>
                      <span>{tenant.userCount || 0}</span>
                    </div>
                    {(tenant.troncMasterMode || tenant.employerDistributionEnabled) && (
<div className="flex items-center justify-between text-sm">
       <span className="text-muted-foreground">Mode</span>
           <span className={`badge text-xs ${tenant.employerDistributionEnabled ? "badge-warning" : "badge-primary"}`}>
    {tenant.employerDistributionEnabled ? "Employer Distribution" : "TRONC Master"}
         </span>
    </div>
                    )}
       <div className="flex items-center justify-between text-sm">
   <span className="text-muted-foreground">Created</span>
                      <span>
                        {new Date(tenant.createdOn).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 border-t border-border flex gap-2">
                    <button
                      className="btn btn-outline btn-sm flex-1"
                      onClick={() => setEditingTenant(tenant)}
                    >
                      <Icon name="Pencil" size={14} />
                      Edit
                    </button>
                    <button
                      className="btn btn-outline btn-sm text-error"
                      onClick={() => handleDeleteTenant(tenant.id, tenant.name)}
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Add/Edit Modal */}
        {(isAddModalOpen || editingTenant) && (
          <TenantFormModal
            tenant={editingTenant}
            onClose={() => {
              setIsAddModalOpen(false);
              setEditingTenant(null);
            }}
            onSave={() => {
              setIsAddModalOpen(false);
              setEditingTenant(null);
              fetchTenants();
              showNotification(
                editingTenant
                  ? "Tenant updated successfully"
                  : "Tenant created successfully",
              );
            }}
          />
        )}
      </div>
    </>
  );
};

const TenantFormModal = ({ tenant, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: tenant?.name || "",
    slug: tenant?.slug || "",
    contactEmail: tenant?.contactEmail || "",
    contactPhone: tenant?.contactPhone || "",
    addressLine1: tenant?.addressLine1 || "",
    city: tenant?.city || "",
    postCode: tenant?.postCode || "",
    subscriptionPlan: tenant?.subscriptionPlan || "Standard",
    maxUsers: tenant?.maxUsers || 10,
    maxEmployees: tenant?.maxEmployees || 100,
    isActive: tenant?.isActive ?? true,
    // Distribution mode: "none" | "troncMaster" | "employerDistribution"
    distributionMode: tenant
      ? (tenant.troncMasterMode
          ? (tenant.employerDistributionEnabled ? "employerDistribution" : "troncMaster")
          : "none")
      : "none",
    // Admin invitation fields (only for new tenants)
    adminEmail: "",
    adminFirstName: "",
    adminLastName: "",
    // TRONC Master invitation fields (only for new tenants with troncMaster mode)
    inviteTroncMasterNow: false,
    troncMasterEmail: "",
    troncMasterFirstName: "",
    troncMasterLastName: "",
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // 'details' | 'integrations'

  // Integration state
  const [integrationsOverview, setIntegrationsOverview] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [integrationForm, setIntegrationForm] = useState({
    apiKey: "",
    baseUrl: "",
    username: "",
    password: "",
  });

  useEffect(() => {
    if (tenant?.id) {
      fetchIntegrations();
    }
  }, [tenant?.id]);

  const fetchIntegrations = async () => {
    if (!tenant?.id) return;
    setIntegrationsLoading(true);
    try {
      const response = await integrationsApi.getSettingsOverview({
        tenantId: tenant.id,
      });
      if (response.success) {
        setIntegrationsOverview(response.data);
      }
    } catch (err) {
      console.error("Failed to load integrations:", err);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.slug) newErrors.slug = "Slug is required";
    if (!formData.contactEmail)
   newErrors.contactEmail = "Contact email is required";

    // Validate admin invitation if email is provided
 if (formData.adminEmail && !tenant) {
      if (!formData.adminFirstName) newErrors.adminFirstName = "Admin first name is required";
      if (!formData.adminLastName) newErrors.adminLastName = "Admin last name is required";
    }

    // Validate TRONC Master invitation if they opted to invite now
    if (formData.distributionMode === "troncMaster" && formData.inviteTroncMasterNow && !tenant) {
      if (!formData.troncMasterEmail) newErrors.troncMasterEmail = "TRONC Master email is required";
  if (!formData.troncMasterFirstName) newErrors.troncMasterFirstName = "TRONC Master first name is required";
      if (!formData.troncMasterLastName) newErrors.troncMasterLastName = "TRONC Master last name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Derive the backend flags from distributionMode
    const troncMasterMode = formData.distributionMode === "troncMaster" || formData.distributionMode === "employerDistribution";
    const employerDistributionEnabled = formData.distributionMode === "employerDistribution";

    setIsSaving(true);
    try {
      if (tenant) {
 await tenantsApi.update(tenant.id, {
   ...formData,
     troncMasterMode,
        employerDistributionEnabled,
     });
  } else {
        // Include admin and TRONC Master invitation fields for new tenants
        const sendTroncMasterInvite = formData.distributionMode === "troncMaster" && formData.inviteTroncMasterNow;
        await tenantsApi.create({
  ...formData,
     adminEmail: formData.adminEmail || null,
          adminFirstName: formData.adminFirstName || null,
          adminLastName: formData.adminLastName || null,
      troncMasterMode,
          employerDistributionEnabled,
          troncMasterEmail: sendTroncMasterInvite ? (formData.troncMasterEmail || null) : null,
          troncMasterFirstName: sendTroncMasterInvite ? (formData.troncMasterFirstName || null) : null,
          troncMasterLastName: sendTroncMasterInvite ? (formData.troncMasterLastName || null) : null,
        });
      }
      onSave();
    } catch (err) {
setErrors({ submit: err.message || "Failed to save tenant" });
    } finally {
  setIsSaving(false);
    }
  };

  const handleConfigureIntegration = (type, provider) => {
    setIntegrationForm({
      apiKey: "",
      baseUrl: "",
      username: "",
      password: "",
    });
    setEditingIntegration({ type, provider });
  };

  const handleSaveIntegration = async (e) => {
    e.preventDefault();
    if (!editingIntegration || !tenant?.id) return;

    setIsSaving(true);
    try {
      await integrationsApi.saveSettings({
        tenantId: tenant.id,
        integrationProviderId: editingIntegration.provider.providerId,
        isEnabled: true,
        apiKey: integrationForm.apiKey || null,
        baseUrl: integrationForm.baseUrl || null,
        username: integrationForm.username || null,
        password: integrationForm.password || null,
      });

      setEditingIntegration(null);
      fetchIntegrations();
    } catch (err) {
      setErrors({ submit: err.message || "Failed to configure integration" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisableIntegration = async (settingsId) => {
    if (!confirm("Are you sure you want to disable this integration?")) return;

    try {
      await integrationsApi.deleteSettings(settingsId);
      fetchIntegrations();
    } catch (err) {
      setErrors({ submit: err.message || "Failed to disable integration" });
    }
  };

  const getActiveProvider = (providers) => {
    return providers?.find((p) => p.isEnabled && p.isConfigured);
  };

  // Auto-generate slug from name
  const handleNameChange = (name) => {
    setFormData({
      ...formData,
      name,
      slug: !tenant
        ? name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "")
        : formData.slug,
    });
  };

  const activeHrProvider = getActiveProvider(integrationsOverview?.hrProviders);
  const activeTillProvider = getActiveProvider(
    integrationsOverview?.tillProviders,
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[1200]" onClick={onClose} />
      <div className="fixed inset-0 z-[1201] flex items-center justify-center p-4">
        <div
          className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-heading font-semibold">
              {tenant ? "Edit Tenant" : "Add New Tenant"}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-md">
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Tabs - only show for editing existing tenant */}
          {tenant && (
            <div className="flex border-b border-border">
              <button
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === "details"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setActiveTab("details")}
              >
                Details
              </button>
              <button
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === "integrations"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setActiveTab("integrations")}
              >
                Integrations
              </button>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === "details" && (
            <form
              onSubmit={handleSubmit}
              className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-4"
            >
              {errors.submit && (
                <div className="alert alert-error">
                  <Icon name="AlertCircle" size={20} />
                  <span>{errors.submit}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Company Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className={`input ${errors.name ? "border-error" : ""}`}
                  />
                </div>
                <div>
                  <label className="label">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    className={`input ${errors.slug ? "border-error" : ""}`}
                    disabled={!!tenant}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Used in URLs
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Contact Email *</label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, contactEmail: e.target.value })
                    }
                    className={`input ${errors.contactEmail ? "border-error" : ""}`}
                  />
                </div>
                <div>
                  <label className="label">Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) =>
                      setFormData({ ...formData, contactPhone: e.target.value })
                    }
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="label">Address</label>
                <input
                  type="text"
                  value={formData.addressLine1}
                  onChange={(e) =>
                    setFormData({ ...formData, addressLine1: e.target.value })
                  }
                  className="input"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Post Code</label>
                  <input
                    type="text"
                    value={formData.postCode}
                    onChange={(e) =>
                      setFormData({ ...formData, postCode: e.target.value })
                    }
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Subscription Plan</label>
                  <select
                    value={formData.subscriptionPlan}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        subscriptionPlan: e.target.value,
                      })
                    }
                    className="input"
                  >
                    <option value="Starter">Starter</option>
                    <option value="Standard">Standard</option>
                    <option value="Professional">Professional</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="label">Max Users</label>
                  <input
                    type="number"
                    value={formData.maxUsers}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maxUsers: parseInt(e.target.value) || 10,
                      })
                    }
                    className="input"
                    min="1"
                  />
                </div>
                <div>
                  <label className="label">Max Employees</label>
                  <input
                    type="number"
                    value={formData.maxEmployees}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maxEmployees: parseInt(e.target.value) || 100,
                      })
                    }
                    className="input"
                    min="1"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="w-5 h-5"
                />
                <label htmlFor="isActive" className="cursor-pointer">
                  <span className="font-medium">Active Tenant</span>
                  <p className="text-sm text-muted-foreground">
                    Inactive tenants cannot access the system
                  </p>
                </label>
              </div>

              {/* Distribution Mode */}
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-4">
  <h3 className="text-sm font-semibold flex items-center gap-2">
        <Icon name="Shield" size={16} className="text-primary" />
       Distribution Mode
          </h3>
     <p className="text-xs text-muted-foreground">
     Choose how TRONC distribution is managed for this tenant.
</p>

          {/* Option 1: No special mode */}
      <label
   htmlFor="mode-none"
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
   formData.distributionMode === "none"
    ? "bg-muted border-primary/40"
    : "border-border hover:bg-muted/50"
}`}
        >
  <input
      type="radio"
              id="mode-none"
        name="distributionMode"
              value="none"
         checked={formData.distributionMode === "none"}
         onChange={() =>
         setFormData({
     ...formData,
  distributionMode: "none",
  inviteTroncMasterNow: false,
   })
            }
        className="w-4 h-4 mt-0.5"
            />
            <div className="flex-1">
       <span className="font-medium">Standard</span>
         <p className="text-sm text-muted-foreground">
     Any admin or manager can distribute TRONC directly.
              </p>
      </div>
      </label>

      {/* Option 2: TRONC Master */}
          <label
        htmlFor="mode-troncMaster"
     className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
      formData.distributionMode === "troncMaster"
? "bg-primary/10 border-primary/40"
    : "border-border hover:bg-muted/50"
          }`}
          >
    <input
   type="radio"
              id="mode-troncMaster"
  name="distributionMode"
  value="troncMaster"
              checked={formData.distributionMode === "troncMaster"}
              onChange={() =>
  setFormData({
      ...formData,
          distributionMode: "troncMaster",
        })
     }
        className="w-4 h-4 mt-0.5"
            />
     <div className="flex-1">
         <span className="font-medium">TRONC Master</span>
              <p className="text-sm text-muted-foreground">
    A dedicated TRONC Master must be invited. Only the TRONC
        Master can build rules and distribute TRONC. Managers can
  distribute using the current rule but must send overrides
        for approval.
              </p>
            </div>
          </label>

          {/* Option 3: Employer Distribution */}
  <label
   htmlFor="mode-employerDistribution"
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              formData.distributionMode === "employerDistribution"
            ? "bg-warning/10 border-warning/40"
      : "border-border hover:bg-muted/50"
  }`}
   >
          <input
     type="radio"
 id="mode-employerDistribution"
       name="distributionMode"
      value="employerDistribution"
   checked={formData.distributionMode === "employerDistribution"}
  onChange={() =>
     setFormData({
      ...formData,
   distributionMode: "employerDistribution",
inviteTroncMasterNow: false,
          })
    }
         className="w-4 h-4 mt-0.5"
            />
            <div className="flex-1">
      <span className="font-medium text-warning">Employer Distribution</span>
           <p className="text-sm text-muted-foreground">
          <strong>Warning:</strong> Allows the employer (Tenant Admin/Manager) to
                distribute and create rules directly, bypassing the TRONC Master.
       This may have NI (National Insurance) consequences as HMRC may consider
            employer-distributed TRONC as part of earnings.
    </p>
            </div>
   </label>
        </div>

              {/* Admin Invitation Fields (only for new tenants) */}
              {!tenant && (
                <div className="p-4 bg-muted rounded-lg border border-muted">
                  <h3 className="text-sm font-semibold mb-3">
                    Invite Admin User
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Admin Email</label>
                      <input
                        type="email"
                        value={formData.adminEmail}
                        onChange={(e) =>
                          setFormData({ ...formData, adminEmail: e.target.value })
                        }
                        className={`input ${
                          errors.adminEmail ? "border-error" : ""
                        }`}
                        placeholder="e.g. admin@example.com"
                      />
                    </div>
                    <div>
                      <label className="label">First Name</label>
                      <input
                        type="text"
                        value={formData.adminFirstName}
                        onChange={(e) =>
                          setFormData({ ...formData, adminFirstName: e.target.value })
                        }
                        className={`input ${
                          errors.adminFirstName ? "border-error" : ""
                        }`}
                      />
                    </div>
                    <div>
                      <label className="label">Last Name</label>
                      <input
                        type="text"
                        value={formData.adminLastName}
                        onChange={(e) =>
                          setFormData({ ...formData, adminLastName: e.target.value })
                        }
                        className={`input ${
                          errors.adminLastName ? "border-error" : ""
                        }`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TRONC Master Invitation Fields (only for new tenants with troncMaster mode) */}
  {!tenant && formData.distributionMode === "troncMaster" && (
           <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
 <div className="flex items-start gap-3">
          <input
   type="checkbox"
    id="inviteTroncMasterNow"
   checked={formData.inviteTroncMasterNow}
     onChange={(e) =>
    setFormData({ ...formData, inviteTroncMasterNow: e.target.checked })
   }
      className="w-5 h-5 mt-0.5"
              />
         <label htmlFor="inviteTroncMasterNow" className="cursor-pointer flex-1">
    <span className="font-medium flex items-center gap-2">
               <Icon name="Shield" size={16} className="text-primary" />
      Invite a TRONC Master now?
                </span>
             <p className="text-xs text-muted-foreground mt-1">
             The TRONC Master is independent of the employer. By accepting the invitation, they confirm they do not have hiring authority, are not a decision maker, and will act independently of employer influence (HMRC requirement).
         </p>
              </label>
 </div>

            {formData.inviteTroncMasterNow && (
   <div className="ml-8 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
       <div>
        <label className="label">TRONC Master Email *</label>
             <input
    type="email"
         value={formData.troncMasterEmail}
         onChange={(e) =>
 setFormData({ ...formData, troncMasterEmail: e.target.value })
       }
        className={`input ${errors.troncMasterEmail ? "border-error" : ""}`}
             placeholder="e.g. troncmaster@example.com"
               />
         {errors.troncMasterEmail && (
<p className="text-xs text-error mt-1">{errors.troncMasterEmail}</p>
            )}
           </div>
        <div>
       <label className="label">First Name *</label>
            <input
             type="text"
   value={formData.troncMasterFirstName}
          onChange={(e) =>
   setFormData({ ...formData, troncMasterFirstName: e.target.value })
           }
 className={`input ${errors.troncMasterFirstName ? "border-error" : ""}`}
      />
   {errors.troncMasterFirstName && (
         <p className="text-xs text-error mt-1">{errors.troncMasterFirstName}</p>
     )}
      </div>
      <div>
           <label className="label">Last Name *</label>
          <input
        type="text"
       value={formData.troncMasterLastName}
          onChange={(e) =>
        setFormData({ ...formData, troncMasterLastName: e.target.value })
           }
    className={`input ${errors.troncMasterLastName ? "border-error" : ""}`}
            />
    {errors.troncMasterLastName && (
      <p className="text-xs text-error mt-1">{errors.troncMasterLastName}</p>
 )}
       </div>
            </div>
    </div>
   )}

        {!formData.inviteTroncMasterNow && (
        <p className="text-xs text-muted-foreground ml-8">
    <em>You can invite the TRONC Master later from User Management.</em>
         </p>
            )}
          </div>
        )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSaving}
                >
                  {isSaving
                    ? "Saving..."
                    : tenant
                      ? "Update Tenant"
                      : "Create Tenant"}
                </button>
              </div>
            </form>
          )}

          {/* Integrations Tab */}
          {activeTab === "integrations" && tenant && (
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
              {integrationsLoading ? (
                <div className="text-center py-8">
                  <div className="spinner mx-auto" />
                  <p className="text-muted-foreground mt-2">
                    Loading integrations...
                  </p>
                </div>
              ) : (
                <>
                  {/* HR Integration */}
                  <div className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon
                            name="Users"
                            size={20}
                            className="text-primary"
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold">HR System</h3>
                          <p className="text-sm text-muted-foreground">
                            Sync employees, job roles, and clock records
                          </p>
                        </div>
                      </div>
                      {activeHrProvider && (
                        <span className="badge badge-success">Connected</span>
                      )}
                    </div>

                    {activeHrProvider ? (
                      <div className="bg-muted/50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">
                              {activeHrProvider.providerName}
                            </p>
                            {activeHrProvider.lastSyncedOn && (
                              <p className="text-xs text-muted-foreground">
                                Last synced:{" "}
                                {new Date(
                                  activeHrProvider.lastSyncedOn,
                                ).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() =>
                                handleConfigureIntegration(
                                  "hr",
                                  activeHrProvider,
                                )
                              }
                            >
                              Configure
                            </button>
                            <button
                              className="btn btn-sm btn-ghost text-error"
                              onClick={() =>
                                handleDisableIntegration(
                                  activeHrProvider.settingsId,
                                )
                              }
                            >
                              Disconnect
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {integrationsOverview?.hrProviders
                          ?.filter((p) => p.isProviderActive)
                          .map((provider) => (
                            <button
                              key={provider.providerId}
                              className="flex items-center gap-3 p-3 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
                              onClick={() =>
                                handleConfigureIntegration("hr", provider)
                              }
                            >
                              <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                <Icon name="Link" size={16} />
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {provider.providerName}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {provider.providerCode}
                                </p>
                              </div>
                            </button>
                          ))}
                        {(!integrationsOverview?.hrProviders ||
                          integrationsOverview.hrProviders.length === 0) && (
                          <p className="text-sm text-muted-foreground col-span-2">
                            No HR providers available.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Till Integration */}
                  <div className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                          <Icon
                            name="PoundSterling"
                            size={20}
                            className="text-secondary"
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold">Till / POS System</h3>
                          <p className="text-sm text-muted-foreground">
                            Sync sales, tips, and TRONC data
                          </p>
                        </div>
                      </div>
                      {activeTillProvider && (
                        <span className="badge badge-success">Connected</span>
                      )}
                    </div>

                    {activeTillProvider ? (
                      <div className="bg-muted/50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">
                              {activeTillProvider.providerName}
                            </p>
                            {activeTillProvider.lastSyncedOn && (
                              <p className="text-xs text-muted-foreground">
                                Last synced:{" "}
                                {new Date(
                                  activeTillProvider.lastSyncedOn,
                                ).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() =>
                                handleConfigureIntegration(
                                  "till",
                                  activeTillProvider,
                                )
                              }
                            >
                              Configure
                            </button>
                            <button
                              className="btn btn-sm btn-ghost text-error"
                              onClick={() =>
                                handleDisableIntegration(
                                  activeTillProvider.settingsId,
                                )
                              }
                            >
                              Disconnect
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {integrationsOverview?.tillProviders
                          ?.filter((p) => p.isProviderActive)
                          .map((provider) => (
                            <button
                              key={provider.providerId}
                              className="flex items-center gap-3 p-3 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
                              onClick={() =>
                                handleConfigureIntegration("till", provider)
                              }
                            >
                              <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                <Icon name="Link" size={16} />
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {provider.providerName}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {provider.providerCode}
                                </p>
                              </div>
                            </button>
                          ))}
                        {(!integrationsOverview?.tillProviders ||
                          integrationsOverview.tillProviders.length === 0) && (
                          <p className="text-sm text-muted-foreground col-span-2">
                            No Till providers available.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Integration Configuration Modal */}
      {editingIntegration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1300] p-4">
          <div className="bg-card rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">
                  Configure {editingIntegration.provider.providerName}
                </h2>
                <button
                  onClick={() => setEditingIntegration(null)}
                  className="p-2 hover:bg-muted rounded-lg"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSaveIntegration} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  API Key
                </label>
                <input
                  type="password"
                  value={integrationForm.apiKey}
                  onChange={(e) =>
                    setIntegrationForm({
                      ...integrationForm,
                      apiKey: e.target.value,
                    })
                  }
                  className="input w-full"
                  placeholder="Enter API key"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Base URL
                </label>
                <input
                  type="url"
                  value={integrationForm.baseUrl}
                  onChange={(e) =>
                    setIntegrationForm({
                      ...integrationForm,
                      baseUrl: e.target.value,
                    })
                  }
                  className="input w-full"
                  placeholder="https://api.example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Username (optional)
                </label>
                <input
                  type="text"
                  value={integrationForm.username}
                  onChange={(e) =>
                    setIntegrationForm({
                      ...integrationForm,
                      username: e.target.value,
                    })
                  }
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Password (optional)
                </label>
                <input
                  type="password"
                  value={integrationForm.password}
                  onChange={(e) =>
                    setIntegrationForm({
                      ...integrationForm,
                      password: e.target.value,
                    })
                  }
                  className="input w-full"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => setEditingIntegration(null)}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="btn btn-primary"
                >
                  {isSaving ? "Saving..." : "Connect"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default TenantManagement;
